import React from 'react';
import { motion } from 'framer-motion';

interface MetricCardProps {
    title: string;
    value: string | number;
    change?: number;
    icon: string;
    colorClass: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, icon, colorClass }) => {
    const formattedChange = change !== undefined ? change.toFixed(1) : null;
    const isPositive = change !== undefined && change >= 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
        >
            <div className="flex items-center justify-between">
                <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {title}
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                        {value}
                    </p>
                    {formattedChange !== null && (
                        <div className="flex items-center mt-2">
                            <span className={`text-sm font-medium ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {isPositive ? '↑' : '↓'} {Math.abs(parseFloat(formattedChange))}%
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                vs período anterior
                            </span>
                        </div>
                    )}
                </div>
                <div className={`w-16 h-16 rounded-full ${colorClass} bg-opacity-10 dark:bg-opacity-20 flex items-center justify-center`}>
                    <span className={`material-symbols-outlined text-3xl ${colorClass}`}>
                        {icon}
                    </span>
                </div>
            </div>
        </motion.div>
    );
};

interface MetricsCardsProps {
    totalSearches: number;
    totalDispatches: number;
    totalTokens: number;
    totalCost: number;
    searchesChange?: number;
    dispatchesChange?: number;
    tokensChange?: number;
    costChange?: number;
}

const MetricsCards: React.FC<MetricsCardsProps> = ({
    totalSearches,
    totalDispatches,
    totalTokens,
    totalCost,
    searchesChange,
    dispatchesChange,
    tokensChange,
    costChange,
}) => {
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
        }).format(value);
    };

    const formatNumber = (value: number) => {
        return new Intl.NumberFormat('pt-BR').format(value);
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <MetricCard
                title="Total de Pesquisas"
                value={formatNumber(totalSearches)}
                change={searchesChange}
                icon="search"
                colorClass="text-blue-600"
            />
            <MetricCard
                title="Total de Disparos"
                value={formatNumber(totalDispatches)}
                change={dispatchesChange}
                icon="send"
                colorClass="text-green-600"
            />
            <MetricCard
                title="Tokens Usados"
                value={formatNumber(totalTokens)}
                change={tokensChange}
                icon="auto_awesome"
                colorClass="text-purple-600"
            />
            <MetricCard
                title="Custo Total"
                value={formatCurrency(totalCost)}
                change={costChange}
                icon="payments"
                colorClass="text-orange-600"
            />
        </div>
    );
};

export default MetricsCards;
